// CMO IA - Interface principale avec Centre de Decision et Chat
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Brain,
  MessageSquare,
  Zap,
  LayoutDashboard,
  Target,
  FileText,
  Layers,
  Trash2,
  Eye,
  Send,
  RefreshCw,
  Loader2,
  Linkedin,
  Facebook,
  Globe,
  Mail,
  CheckCircle
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";

// Import des composants
import CMODecisionCenter from "./CMODecisionCenter";
import CMOChat from "./CMOChat";
import MissionsMarketingPage from "./MissionsMarketingPage";

const API = process.env.REACT_APP_BACKEND_URL;

// Helper pour les icônes de canal
const CanalIcon = ({ canal, className }) => {
  switch (canal?.toLowerCase()) {
    case 'linkedin': return <Linkedin className={className} />;
    case 'facebook': return <Facebook className={className} />;
    case 'blog': case 'seo': return <Globe className={className} />;
    case 'newsletter': case 'email': return <Mail className={className} />;
    default: return <FileText className={className} />;
  }
};

// Badge de statut
const StatusBadge = ({ status }) => {
  const styles = {
    generated: "bg-amber-500/20 text-amber-400",
    approved: "bg-blue-500/20 text-blue-400",
    published: "bg-emerald-500/20 text-emerald-400",
    draft: "bg-zinc-500/20 text-zinc-400"
  };
  return (
    <Badge className={styles[status] || styles.draft}>
      {status === 'generated' ? 'Généré' : status === 'published' ? 'Publié' : status}
    </Badge>
  );
};

export default function CMOAgent() {
  const [activeMode, setActiveMode] = useState("decision");
  const [contents, setContents] = useState([]);
  const [verticales, setVerticales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewContent, setViewContent] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("session_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Charger les contenus et verticales
  const loadData = useCallback(async () => {
    if (activeMode !== "contents" && activeMode !== "verticales") return;
    
    setLoading(true);
    try {
      const [contentsRes, verticalesRes] = await Promise.all([
        axios.get(`${API}/api/marketing/contents`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/marketing/verticales`, { headers: getAuthHeaders() })
      ]);
      setContents(contentsRes.data.contents || []);
      setVerticales(verticalesRes.data.verticales || []);
    } catch (err) {
      console.error("Erreur chargement données:", err);
    } finally {
      setLoading(false);
    }
  }, [activeMode, getAuthHeaders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Supprimer un contenu
  const handleDeleteContent = async (content) => {
    if (!confirm(`Supprimer définitivement "${content.title}" ?`)) return;
    
    try {
      await axios.delete(`${API}/api/marketing/contents/${content.id}`, {
        headers: getAuthHeaders()
      });
      loadData();
    } catch (err) {
      console.error("Erreur suppression:", err);
      alert("Erreur lors de la suppression");
    }
  };

  // Publier un contenu
  const handlePublish = async (content) => {
    const url = prompt("URL de publication:");
    if (!url) return;
    
    try {
      await axios.post(
        `${API}/api/marketing/contents/${content.id}/publish`,
        null,
        { headers: getAuthHeaders(), params: { published_url: url } }
      );
      loadData();
    } catch (err) {
      console.error("Erreur publication:", err);
    }
  };

  // Supprimer une verticale
  const handleDeleteVerticale = async (verticale) => {
    if (!confirm(`Supprimer la verticale "${verticale.nom}" ? Cette action est irréversible.`)) return;
    
    try {
      await axios.delete(`${API}/api/marketing/verticales/${verticale.code}`, {
        headers: getAuthHeaders()
      });
      loadData();
    } catch (err) {
      console.error("Erreur suppression verticale:", err);
      alert("Erreur lors de la suppression");
    }
  };

  // Toggle activation verticale
  const handleToggleVerticale = async (verticale) => {
    try {
      await axios.put(`${API}/api/marketing/verticales/${verticale.code}`, {
        ...verticale,
        enabled: !verticale.enabled
      }, {
        headers: getAuthHeaders()
      });
      loadData();
    } catch (err) {
      console.error("Erreur modification verticale:", err);
    }
  };

  return (
    <div className="h-full">
      <Tabs value={activeMode} onValueChange={setActiveMode} className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="bg-zinc-900/50 border border-white/10">
            <TabsTrigger 
              value="decision" 
              className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400"
            >
              <Zap className="w-4 h-4 mr-2" />
              Centre de Décision
            </TabsTrigger>
            <TabsTrigger 
              value="missions" 
              className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400"
            >
              <Target className="w-4 h-4 mr-2" />
              Missions
            </TabsTrigger>
            <TabsTrigger 
              value="contents" 
              className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400"
            >
              <FileText className="w-4 h-4 mr-2" />
              Contenus
            </TabsTrigger>
            <TabsTrigger 
              value="verticales" 
              className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400"
            >
              <Layers className="w-4 h-4 mr-2" />
              Verticales
            </TabsTrigger>
            <TabsTrigger 
              value="chat" 
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Chat CMO
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="decision" className="flex-1 mt-0">
          <CMODecisionCenter />
        </TabsContent>

        <TabsContent value="missions" className="flex-1 mt-0">
          <MissionsMarketingPage embedded={true} />
        </TabsContent>

        {/* Contenus Tab */}
        <TabsContent value="contents" className="flex-1 mt-0">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Contenus générés ({contents.length})</h3>
            <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
            </div>
          ) : contents.length === 0 ? (
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-8 text-center">
                <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-white font-medium">Aucun contenu</p>
                <p className="text-sm text-zinc-500 mt-1">Les contenus générés par le CMO apparaîtront ici</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contents.map(content => (
                <Card key={content.id} className="bg-zinc-900/50 border-white/10 hover:border-white/20 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center">
                          <CanalIcon canal={content.canal} className="w-4 h-4 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 uppercase">{content.verticale}</p>
                          <p className="text-xs text-zinc-400">{content.canal}</p>
                        </div>
                      </div>
                      <StatusBadge status={content.status} />
                    </div>
                    
                    <h3 className="text-white font-medium mb-2 line-clamp-2">{content.title}</h3>
                    
                    {content.excerpt && (
                      <p className="text-sm text-zinc-400 line-clamp-2 mb-3">{content.excerpt}</p>
                    )}
                    
                    <div className="flex items-center justify-between text-xs text-zinc-500">
                      <span>{new Date(content.created_at).toLocaleDateString('fr-FR')}</span>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setViewContent(content)}>
                          <Eye className="w-3 h-3" />
                        </Button>
                        {(content.status === 'generated' || content.status === 'approved') && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-green-400" onClick={() => handlePublish(content)}>
                            <Send className="w-3 h-3" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10" 
                          onClick={() => handleDeleteContent(content)}
                          data-testid={`delete-content-${content.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Verticales Tab */}
        <TabsContent value="verticales" className="flex-1 mt-0">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Verticales ({verticales.length})</h3>
            <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
            </div>
          ) : verticales.length === 0 ? (
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-8 text-center">
                <Layers className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-white font-medium">Aucune verticale</p>
                <p className="text-sm text-zinc-500 mt-1">Créez des verticales pour organiser vos contenus</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {verticales.map(v => (
                <Card key={v.id} className="bg-zinc-900/50 border-white/10">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white">{v.nom}</h3>
                        <p className="text-sm text-zinc-400">{v.description}</p>
                      </div>
                      <Badge className={v.enabled ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-500/20 text-zinc-400"}>
                        {v.enabled ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Mots-clés SEO</p>
                        <div className="flex flex-wrap gap-1">
                          {v.keywords?.map((kw, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Canaux</p>
                        <div className="flex gap-2">
                          {v.canaux_actifs?.map(c => (
                            <div key={c} className="flex items-center gap-1 px-2 py-1 bg-zinc-800 rounded">
                              <CanalIcon canal={c} className="w-3 h-3 text-zinc-400" />
                              <span className="text-xs text-zinc-400">{c}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Actions verticale */}
                      <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5">
                        <Button
                          size="sm"
                          variant="ghost"
                          className={v.enabled ? "text-amber-400 hover:bg-amber-500/10" : "text-emerald-400 hover:bg-emerald-500/10"}
                          onClick={() => handleToggleVerticale(v)}
                          data-testid={`toggle-verticale-${v.code}`}
                        >
                          {v.enabled ? "Désactiver" : "Activer"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:bg-red-500/10"
                          onClick={() => handleDeleteVerticale(v)}
                          data-testid={`delete-verticale-${v.code}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="chat" className="flex-1 mt-0">
          <CMOChat />
        </TabsContent>
      </Tabs>

      {/* Dialog pour voir un contenu */}
      <Dialog open={!!viewContent} onOpenChange={() => setViewContent(null)}>
        <DialogContent className="max-w-2xl bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">{viewContent?.title}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-4">
              <Badge>{viewContent?.verticale}</Badge>
              <Badge variant="outline">{viewContent?.canal}</Badge>
              <StatusBadge status={viewContent?.status} />
            </div>
            <div className="prose prose-invert max-w-none">
              <div className="whitespace-pre-wrap text-zinc-300 text-sm bg-zinc-800/50 p-4 rounded-lg max-h-96 overflow-y-auto">
                {viewContent?.content}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
